import { ContractType } from '@app/features/contracts/contract.model';
import { ValidationRequestType } from '@app/features/contracts/validation/validation-request.model';
import { ValidationRequestTypeLabelPipe } from '@app/features/contracts/validation/validation-request-type-label-pipe';
import { DocumentTag, DocumentTags } from '@app/features/documents/document.model';

/**
 * Le réglage des notifications, tel que l'expose `/notification-settings` (cadrage 2026-016,
 * RG-notification-reglage-des-destinataires).
 */

export const NotificationEvents = [
  'VALIDATION_REQUESTED',
  'VALIDATION_ACCEPTED',
  'VALIDATION_REFUSED',
  'VALIDATION_CANCELLED',
  'CONTRACT_READY_FOR_SIGNATURE',
  'SIGNATURE_PENDING',
  'CONTRACT_SIGNED',
  'AMENDMENT_SIGNED',
  'CONTRACT_CANCELED',
  'CONTRACT_DELETED',
  'DOCUMENT_DEPOSITED',
  'DOCUMENT_PENDING',
  'RESIDENT_PERMIT_EXPIRING',
  'SOCIAL_SECURITY_NUMBER_UPDATED',
] as const;
export type NotificationEvent = (typeof NotificationEvents)[number];

export type NotificationFamily = 'VALIDATION' | 'SIGNATURE' | 'CONTRACT' | 'DOCUMENTS' | 'EMPLOYEE';

/** Ce qui, en plus de la société et du type de contrat, précise une ligne. */
export type NotificationPrecision = 'NONE' | 'VALIDATION_TYPE' | 'DOCUMENT_TAG';

export type DesignationScope = 'CONTRACT_COMPANY' | 'ALL_COMPANIES';

export type ContractRelation =
  'CONTRACT_CREATOR' | 'REQUEST_AUTHOR' | 'REQUEST_RECIPIENT' | 'DP_REQUEST_RECIPIENT';

export type ReminderFrequency = 'FIXED' | 'DEGRESSIVE';

/**
 * Les rôles qu'une ligne peut désigner, sous leur nom Keycloak. L'API dit lesquels pour chaque
 * événement (`designableRoles`) ; ce type ne sert qu'à leur donner un libellé.
 */
export type DesignableRole =
  | 'cdt-administration-du-personnel'
  | 'cdt-assistant-de-production'
  | 'cdt-responsable-exploitation'
  | 'cdt-directeur-agence'
  | 'cdt-directeur-production'
  | 'cdt-responsable-rh'
  | 'cdt-directeur-rh'
  | 'cdt-responsable-paie'
  | 'cdt-directeur-admin-financier'
  | 'cdt-directeur-general';

/** Une désignation : soit un profil (`role` et `scope`), soit une `relation` au contrat. */
export interface NotificationDesignation {
  role: DesignableRole | null;
  scope: DesignationScope | null;
  relation: ContractRelation | null;
}

/** La cadence d'une ligne de relance, en jours ouvrés (RG-relance-cadence-reglable). */
export interface ReminderCadence {
  firstReminderDelay: number;
  frequency: ReminderFrequency;
  /** Intervalle d'une fréquence fixe ; absent pour une dégressive. */
  frequencyInterval: number | null;
  /** Absent : sans maximum. */
  maxReminders: number | null;
}

export interface NotificationSettingLine {
  id: string;
  /** La ligne générale n'a aucun critère, et vaut pour tous les contrats. */
  general: boolean;
  companyId: string | null;
  contractType: ContractType | null;
  validationType: ValidationRequestType | null;
  documentTag: DocumentTag | null;
  designations: NotificationDesignation[];
  cadence: ReminderCadence | null;
  createdOn: string; // ISO date string
}

export type NotificationSettingLinePayload = Omit<
  NotificationSettingLine,
  'id' | 'general' | 'createdOn'
>;

/** Le réglage d'un événement ; la ligne générale en tête. */
export interface NotificationEventSetting {
  event: NotificationEvent;
  family: NotificationFamily;
  precision: NotificationPrecision;
  reminder: boolean;
  subscriptionAttribute: string;
  /** Vrai pour les relances dont chaque ligne porte sa cadence ; le titre de séjour garde celle du serveur. */
  cadenceAdjustable: boolean;
  /** Les profils qu'une ligne peut désigner, dans l'ordre de l'API. */
  designableRoles: DesignableRole[];
  /** Les relations au contrat qu'une ligne de cet événement peut désigner. */
  allowedRelations: ContractRelation[];
  lines: NotificationSettingLine[];
}

/** Le cas dont on veut l'aperçu ; un critère absent vaut « aucun ». */
export interface NotificationPreviewQuery {
  companyId: string | null;
  contractType: ContractType | null;
  validationType: ValidationRequestType | null;
  documentTag: DocumentTag | null;
}

/** La valeur d'un abonnement qui ajoute une personne, telle que l'attribut Keycloak la porte. */
export type SubscriptionOption = 'myCompanies' | 'all';

/** Ce qui désigne une personne : une ligne et son profil, ou son abonnement. */
export interface NotificationPreviewReason {
  lineId: string | null;
  role: DesignableRole | null;
  scope: DesignationScope | null;
  subscription: boolean;
  /** Renseignée quand la personne s'ajoute par son abonnement. */
  subscriptionOption: SubscriptionOption | null;
}

export interface NotificationPreviewPerson {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  reasons: NotificationPreviewReason[];
}

/** L'aperçu des destinataires d'un événement pour un cas (RG-notification-apercu-des-destinataires). */
export interface NotificationPreview {
  lines: NotificationSettingLine[];
  recipients: NotificationPreviewPerson[];
  /** Les relations au contrat, nommées sans être résolues : hors d'un contrat, elles ne désignent personne. */
  relations: { lineId: string; relation: ContractRelation }[];
  unsubscribed: NotificationPreviewPerson[];
  withoutEmail: NotificationPreviewPerson[];
}

export const notificationEventLabels: Record<NotificationEvent, string> = {
  VALIDATION_REQUESTED: 'Demande de validation émise',
  VALIDATION_ACCEPTED: 'Demande de validation acceptée',
  VALIDATION_REFUSED: 'Demande de validation refusée',
  VALIDATION_CANCELLED: 'Demande de validation annulée',
  CONTRACT_READY_FOR_SIGNATURE: 'Contrat prêt pour signature',
  SIGNATURE_PENDING: 'Signature en attente',
  CONTRACT_SIGNED: 'Contrat signé',
  AMENDMENT_SIGNED: 'Avenant signé',
  CONTRACT_CANCELED: 'Contrat annulé',
  CONTRACT_DELETED: 'Contrat supprimé',
  DOCUMENT_DEPOSITED: 'Document déposé',
  DOCUMENT_PENDING: 'Document en attente',
  RESIDENT_PERMIT_EXPIRING: 'Titre de séjour arrivant à expiration',
  SOCIAL_SECURITY_NUMBER_UPDATED: 'Numéro de sécurité sociale mis à jour',
};

export const notificationEventDescriptions: Partial<Record<NotificationEvent, string>> = {
  VALIDATION_REFUSED: 'Porte le motif du refus',
  VALIDATION_CANCELLED: "Porte le motif de l'annulation",
  CONTRACT_READY_FOR_SIGNATURE: 'Entrée au statut « Signature en cours »',
  SIGNATURE_PENDING: 'Relance tant que le contrat attend sa signature',
  CONTRACT_SIGNED: "Signature électronique ou dépôt de l'exemplaire signé",
  AMENDMENT_SIGNED: "Signature électronique ou dépôt de l'exemplaire signé d'un avenant",
  DOCUMENT_DEPOSITED: 'Par type de document',
  DOCUMENT_PENDING: "Relance tant qu'un document attendu n'est pas déposé",
  RESIDENT_PERMIT_EXPIRING: 'Relance, à la cadence fixée par le serveur',
};

export const notificationFamilies: { family: NotificationFamily; label: string }[] = [
  { family: 'VALIDATION', label: 'Circuit de validation' },
  { family: 'SIGNATURE', label: 'Signature' },
  { family: 'CONTRACT', label: 'Contrat' },
  { family: 'DOCUMENTS', label: 'Documents' },
  { family: 'EMPLOYEE', label: 'Salarié' },
];

export const roleLabels: Record<DesignableRole, string> = {
  'cdt-administration-du-personnel': 'Administration du personnel',
  'cdt-assistant-de-production': 'Assistant de production',
  'cdt-responsable-exploitation': "Responsable d'exploitation",
  'cdt-directeur-agence': "Directeur d'agence",
  'cdt-directeur-production': 'Directeur de production',
  'cdt-responsable-rh': 'Responsable RH',
  'cdt-directeur-rh': 'Directeur RH',
  'cdt-responsable-paie': 'Responsable paie',
  'cdt-directeur-admin-financier': 'Directeur administratif et financier',
  'cdt-directeur-general': 'Directeur général',
};

export const subscriptionOptionLabels: Record<SubscriptionOption, string> = {
  myCompanies: 'ses sociétés',
  all: 'toutes les sociétés',
};

export const designationScopeLabels: Record<DesignationScope, string> = {
  CONTRACT_COMPANY: 'société du contrat',
  ALL_COMPANIES: 'toutes les sociétés',
};

export const contractRelationLabels: Record<ContractRelation, string> = {
  CONTRACT_CREATOR: 'Créateur du contrat',
  REQUEST_AUTHOR: 'Auteur de la demande',
  REQUEST_RECIPIENT: 'Destinataire de la demande',
  DP_REQUEST_RECIPIENT: 'Destinataire de la demande DP',
};

/** Délai et fréquence fixe : de 1 à 60 jours ouvrés ; le maximum, quand il est fixé, au moins 1. */
export const CADENCE_MIN_DAYS = 1;
export const CADENCE_MAX_DAYS = 60;
export const CADENCE_MIN_REMINDERS = 1;

export function roleLabel(role: DesignableRole): string {
  return roleLabels[role] ?? role;
}

export function designationLabel(designation: NotificationDesignation): string {
  if (designation.relation) {
    return contractRelationLabels[designation.relation];
  }
  if (!designation.role) {
    return '';
  }
  const scope = designation.scope ? ` · ${designationScopeLabels[designation.scope]}` : '';
  return `${roleLabel(designation.role)}${scope}`;
}

export function generalLineOf(
  setting: NotificationEventSetting,
): NotificationSettingLine | undefined {
  return setting.lines.find((line) => line.general);
}

export function particularLinesOf(setting: NotificationEventSetting): NotificationSettingLine[] {
  return setting.lines.filter((line) => !line.general);
}

/** « Ligne générale », puis « Ligne 1 », « Ligne 2 »… dans l'ordre des lignes particulières. */
export function lineLabel(setting: NotificationEventSetting, lineId: string | null): string {
  const particulars = particularLinesOf(setting);
  const rank = particulars.findIndex((line) => line.id === lineId);
  return rank === -1 ? 'Ligne générale' : `Ligne ${rank + 1}`;
}

/** Les critères d'une ligne, en clair : « Société B · CDD A · RIB », ou « Tous les contrats ». */
export function lineCriteria(
  line: NotificationSettingLine,
  companyName: (companyId: string) => string,
): string {
  const criteria = [
    line.companyId ? companyName(line.companyId) : null,
    line.contractType,
    line.validationType
      ? `Échelon ${new ValidationRequestTypeLabelPipe().transform(line.validationType)}`
      : null,
    line.documentTag ? DocumentTags.labels[line.documentTag] : null,
  ].filter((criterion): criterion is string => !!criterion);
  return criteria.length ? criteria.join(' · ') : 'Tous les contrats';
}

export function describeCadence(cadence: ReminderCadence): string {
  const then =
    cadence.frequency === 'FIXED'
      ? `tous les ${cadence.frequencyInterval} j ouvrés`
      : 'dégressif (1, 2, 3, 5, 8… j ouvrés)';
  const max = cadence.maxReminders ? `${cadence.maxReminders} au plus` : 'sans maximum';
  return `1er rappel à ${cadence.firstReminderDelay} j ouvrés, puis ${then} · ${max}`;
}

export function isBusinessDay(date: Date): boolean {
  const day = date.getDay();
  return day !== 0 && day !== 6;
}

/** Un jour ouvré va du lundi au vendredi, jours fériés compris. */
export function addBusinessDays(from: Date, days: number): Date {
  const date = new Date(from.getTime());
  let remaining = days;
  while (remaining > 0) {
    date.setDate(date.getDate() + 1);
    if (isBusinessDay(date)) {
      remaining--;
    }
  }
  return date;
}

/** L'intervalle qui suit le rappel de rang `rank` (à partir de 1) : fixe, ou 1, 2, 3, 5, 8… */
function intervalAfter(cadence: ReminderCadence, rank: number): number {
  if (cadence.frequency === 'FIXED') {
    return cadence.frequencyInterval ?? CADENCE_MIN_DAYS;
  }
  let previous = 1;
  let current = 1;
  for (let i = 1; i < rank; i++) {
    const next = previous + current;
    previous = current;
    current = next;
  }
  return current;
}

/**
 * Les dates des rappels d'une surveillance ouverte le jour `from`, comme le serveur les calcule :
 * chaque rappel se compte depuis le précédent. Sans maximum, on n'en montre que `limit`.
 */
export function reminderSchedule(cadence: ReminderCadence, from: Date, limit = 8): Date[] {
  const count = cadence.maxReminders ? Math.min(cadence.maxReminders, limit) : limit;
  const dates: Date[] = [];
  let date = addBusinessDays(from, cadence.firstReminderDelay);
  for (let rank = 1; rank <= count; rank++) {
    dates.push(date);
    date = addBusinessDays(date, intervalAfter(cadence, rank));
  }
  return dates;
}
